// Tema global del rediseño CrediApp — ver design_handoff_crediapp_redesign/README.md
// Reemplaza los createTheme ad-hoc previos (DrawerApp, Login, Unautorized, ServerOff).
// Uso: const theme = isDarkMode ? darkblueTheme : lightTheme;
import { createTheme } from '@mui/material/styles';

const headingFont = "'Poppins', sans-serif";

const shared = {
	typography: {
		fontFamily: "'Lato', sans-serif",
		h1: { fontFamily: headingFont },
		h2: { fontFamily: headingFont },
		h3: { fontFamily: headingFont },
		h4: { fontFamily: headingFont },
		h5: { fontFamily: headingFont, fontWeight: 600 },
		h6: { fontFamily: headingFont, fontSize: 15.5, fontWeight: 600, letterSpacing: '-.2px' },
		body1: { fontSize: 13.5 },
		body2: { fontSize: 12.5 },
		caption: { fontSize: 11.5 },
		button: { fontFamily: headingFont, textTransform: 'none', fontWeight: 600, fontSize: 13 }
	},
	shape: { borderRadius: 11 }
};

const sharedComponents = (t, mode) => ({
	// Fondo del documento y barras de scroll segun el tema — el .dc.html define
	// ::-webkit-scrollbar; sin esto Windows pinta la barra nativa en claro y se
	// veia una franja blanca al costado y debajo del shell en modo oscuro.
	MuiCssBaseline: {
		styleOverrides: {
			':root': { colorScheme: mode },
			'html, body, #root': { backgroundColor: t.bg },
			'*::-webkit-scrollbar': { width: 10, height: 10 },
			'*::-webkit-scrollbar-track': { background: 'transparent' },
			'*::-webkit-scrollbar-thumb': {
				background: t.chipLine,
				borderRadius: 8,
				border: '2px solid transparent',
				backgroundClip: 'padding-box'
			},
			'*::-webkit-scrollbar-thumb:hover': { background: t.mut2 },
			'*::-webkit-scrollbar-corner': { background: 'transparent' }
		}
	},
	MuiPaper: {
		styleOverrides: {
			root: {
				backgroundImage: 'none',
				borderRadius: 16,
				boxShadow: 'none',
				border: `1px solid ${t.line}`
			}
		}
	},
	// AppBar y Drawer se construyen sobre Paper, asi que heredaban el radio de
	// 16px y el borde de las tarjetas. En el handoff el topbar va a todo lo
	// ancho, sin esquinas redondeadas y con un unico borde inferior; el sidebar
	// tampoco lleva radio.
	MuiAppBar: {
		styleOverrides: {
			root: {
				borderRadius: 0,
				border: 'none',
				borderBottom: `1px solid ${t.line}`,
				backgroundImage: 'none'
			}
		}
	},
	MuiDrawer: {
		styleOverrides: {
			paper: {
				borderRadius: 0,
				border: 'none',
				backgroundImage: 'none'
			}
		}
	},
	MuiButton: {
		styleOverrides: {
			root: { borderRadius: 11, padding: '9px 16px' },
			containedPrimary: {
				backgroundImage: 'linear-gradient(135deg,#12a35a,#0c8a4c)',
				boxShadow: '0 10px 20px -12px rgba(18,163,90,.7)',
				'&:hover': { backgroundImage: 'linear-gradient(135deg,#0c8a4c,#0c8a4c)', boxShadow: '0 10px 20px -12px rgba(18,163,90,.7)' }
			}
		}
	},
	MuiOutlinedInput: {
		styleOverrides: {
			root: {
				borderRadius: 11,
				backgroundColor: t.field,
				color: t.ink,
				'& fieldset': { borderColor: t.fieldLine },
				'&.Mui-focused fieldset': { borderColor: '#12a35a' },
				'& input:-webkit-autofill': {
					WebkitBoxShadow: `0 0 0 100px ${t.field} inset`,
					WebkitTextFillColor: t.ink,
					caretColor: t.ink
				}
			}
		}
	},
	MuiSelect: {
		styleOverrides: {
			icon: { color: t.mut },
			select: { backgroundColor: 'transparent' }
		}
	},
	MuiMenuItem: {
		styleOverrides: {
			root: { color: t.ink }
		}
	},
	MuiInputLabel: {
		styleOverrides: {
			root: { color: t.mut }
		}
	},
	MuiChip: {
		styleOverrides: {
			root: { borderRadius: 20, fontSize: 11, fontWeight: 600, height: 22 }
		}
	},
	MuiPagination: {
		styleOverrides: {
			ul: { gap: 7 }
		}
	},
	MuiPaginationItem: {
		styleOverrides: {
			root: {
				width: 34,
				height: 34,
				minWidth: 34,
				borderRadius: 9,
				fontSize: 13,
				fontWeight: 700,
				backgroundColor: t.field,
				border: `1px solid ${t.fieldLine}`,
				color: t.ink,
				'&:hover': { backgroundColor: t.field },
				'&.Mui-selected': {
					backgroundColor: '#12a35a',
					borderColor: '#12a35a',
					color: '#fff',
					'&:hover': { backgroundColor: '#12a35a' }
				}
			}
		}
	},
	MuiTableCell: {
		styleOverrides: {
			head: {
				background: t.thead,
				color: t.mut,
				fontSize: 11,
				fontWeight: 600,
				textTransform: 'uppercase',
				letterSpacing: '.4px',
				borderBottom: `1px solid ${t.row}`
			},
			root: { borderBottom: `1px solid ${t.row}`, padding: '12px 20px', fontSize: 13 }
		}
	},
	MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, fontSize: 13 } } }
});

export const lightTokens = {
	bg: '#eef1f7',
	card: '#ffffff',
	line: '#e9edf4',
	line2: '#e4e9f1',
	field: '#f7f9fc',
	fieldLine: '#e4e9f1',
	chip: '#f1f4fa',
	chipLine: '#e4e9f1',
	thead: '#f7f9fc',
	row: '#eef1f6',
	soft: '#eef0f3',
	tabbar: '#f1f4fa',
	ink: '#111a2e',
	ink2: '#3a4557',
	ink3: '#5d6a83',
	mut: '#5d6a83',
	mut2: '#95a0b6',
	greenFg: '#00844a',
	greenBg: 'rgba(0,132,74,.12)',
	greenLine: '#b7e0c8',
	redFg: '#d83a3f',
	redBg: 'rgba(216,58,63,.12)',
	redLine: '#f0c2c2',
	amberFg: '#c77f12',
	amberBg: 'rgba(199,127,18,.14)',
	blueFg: '#1d4ed8',
	blueBg: 'rgba(29,78,216,.12)',
	hoverGreen: 'rgba(18,163,90,.06)'
};

export const darkTokens = {
	bg: '#070f22',
	card: '#0f1b38',
	line: '#1d2c4e',
	line2: '#243761',
	field: '#0d1a37',
	fieldLine: '#1d2c4e',
	chip: '#132247',
	chipLine: '#243761',
	thead: '#0d1a37',
	row: '#18274a',
	soft: '#1e3149',
	tabbar: '#132247',
	ink: '#e9eefb',
	ink2: '#c2cede',
	ink3: '#9aabca',
	mut: '#9aabca',
	mut2: '#6a7c9d',
	greenFg: '#33d187',
	greenBg: 'rgba(51,209,135,.16)',
	greenLine: 'rgba(0,199,102,.4)',
	redFg: '#ff6b70',
	redBg: 'rgba(255,107,112,.16)',
	redLine: 'rgba(229,72,77,.4)',
	amberFg: '#ffb84d',
	amberBg: 'rgba(255,184,77,.15)',
	blueFg: '#87acff',
	blueBg: 'rgba(135,172,255,.16)',
	hoverGreen: 'rgba(18,163,90,.06)'
};

export const lightTheme = createTheme({
	...shared,
	palette: {
		mode: 'light',
		primary: { main: '#12a35a', dark: '#0c8a4c', light: '#34c77b', contrastText: '#fff' },
		success: { main: lightTokens.greenFg, light: lightTokens.greenBg },
		error: { main: lightTokens.redFg, light: lightTokens.redBg },
		warning: { main: lightTokens.amberFg, light: lightTokens.amberBg },
		info: { main: lightTokens.blueFg, light: lightTokens.blueBg },
		background: { default: lightTokens.bg, paper: lightTokens.card },
		text: { primary: lightTokens.ink, secondary: lightTokens.mut },
		divider: lightTokens.line
	},
	components: sharedComponents(lightTokens, 'light')
});

export const darkblueTheme = createTheme({
	...shared,
	palette: {
		mode: 'dark',
		primary: { main: '#12a35a', dark: '#0c8a4c', light: '#34c77b', contrastText: '#fff' },
		success: { main: darkTokens.greenFg, light: darkTokens.greenBg },
		error: { main: darkTokens.redFg, light: darkTokens.redBg },
		warning: { main: darkTokens.amberFg, light: darkTokens.amberBg },
		info: { main: darkTokens.blueFg, light: darkTokens.blueBg },
		background: { default: darkTokens.bg, paper: darkTokens.card },
		text: { primary: darkTokens.ink, secondary: darkTokens.mut },
		divider: darkTokens.line
	},
	components: sharedComponents(darkTokens, 'dark')
});

// Constantes fuera del tema (sidebar navy — igual en ambos temas)
export const sidebar = {
	bg: '#0d1730',
	itemColor: '#9aa6bf',
	iconColor: '#7c88a1',
	activeBg: 'rgba(18,163,90,.16)',
	activeBar: '#12a35a',
	activeText: '#a6e8c6',
	sectionColor: '#5c6a86',
	footerBorder: 'rgba(255,255,255,.05)',
	userColor: '#e8ecf6',
	mutedColor: '#7c88a1'
};

export const monoFont = "'Poppins', sans-serif";

export const getAppTheme = (isDarkMode) => (isDarkMode ? darkblueTheme : lightTheme);
export const getTokens = (isDarkMode) => (isDarkMode ? darkTokens : lightTokens);

// Paleta para gráficas Chart.js (barras/línea/pastel). El relleno translúcido
// pensado para fondo blanco (alpha .2) se ve sucio/desaturado sobre el navy
// oscuro, así que el alpha del relleno sube bastante en modo oscuro.
// Colores de serie del handoff: verde primario, teal secundario, ambar,
// verde claro, azul y rojo (README > "Color — Marca y acentos").
const CHART_HUES = [
	[18, 163, 90],
	[16, 163, 163],
	[230, 160, 31],
	[52, 199, 123],
	[29, 78, 216],
	[216, 58, 63]
];

export const chartPalette = (isDarkMode, count = 6) => {
	const fillAlpha = isDarkMode ? 0.55 : 0.2;
	const bg = [];
	const border = [];
	for (let i = 0; i < count; i++) {
		const [r, g, b] = CHART_HUES[i % CHART_HUES.length];
		bg.push(`rgba(${r},${g},${b},${fillAlpha})`);
		border.push(`rgba(${r},${g},${b},1)`);
	}
	return { bg, border };
};

export const chartTextColor = (isDarkMode) => (isDarkMode ? darkTokens.ink2 : lightTokens.ink2);
export const chartGridColor = (isDarkMode) => (isDarkMode ? darkTokens.row : lightTokens.row);
